import { useQuery } from '@tanstack/react-query';
import { BRAND_COLORS } from '@lk/shared';
import { Icon } from '../../components/Icon';
import { Panel } from '../../components/Panel';
import { QuestionText } from '../../components/QuestionText';
import { formatDate } from '../../lib/format';
import { figureFailuresQuery } from '../learner/queries';

/**
 * Малюнки, які не доїхали до екрана дитини.
 *
 * НАВІЩО ЦЕЙ БЛОК ІСНУЄ. `SafeSvg` на зламаній розмітці повертає `null`:
 * дитина бачить порожню рамку, гра йде далі, і ніде не лишається сліду.
 * Саме ця тиша дала попередній поломці з малюнками прожити стільки, скільки
 * вона прожила — 645 питань показували дітям сирий код, і помітив це
 * випадково батько, відкривши розбір помилок.
 *
 * Тепер екран сам повідомляє про свою невдачу, а тут вона видно.
 *
 * Порожньо — це нормальний і бажаний стан. Тому блок мовчить, коли нема про
 * що казати: постійний напис «поломок 0» перетворюється на шум, який
 * перестають читати рівно тоді, коли він нарешті стає важливим.
 */
export function FigureFailures(): React.JSX.Element | null {
  const failures = useQuery({ ...figureFailuresQuery(), retry: false });
  const items = failures.data ?? [];

  if (failures.isLoading || failures.isError || items.length === 0) return null;

  return (
    <Panel className="p-4" style={{ borderColor: `${BRAND_COLORS.red}66` }}>
      <div className="flex items-center gap-2">
        <Icon name="wrong" size={18} />
        <h2 className="text-lg font-black">Малюнки, які не показалися</h2>
      </div>
      <p className="mt-1 text-xs font-semibold text-white/55">
        У цих питаннях дитина побачила порожнє місце замість фігури. Питання варто прибрати або
        полагодити — воно нерозвʼязне.
      </p>

      <div className="mt-3 flex flex-col gap-3">
        {items.map((item) => (
          <div
            key={`${item.itemId}-${item.field}`}
            className="rounded-xl border border-white/10 p-3"
          >
            <div className="flex flex-wrap items-center gap-2 text-[11px] font-black text-white/50 uppercase">
              <span>{item.skillTitle}</span>
              <span className="text-white/30">·</span>
              <span>{item.field === 'passage' ? 'малюнок умови' : 'картинка варіанта'}</span>
              <span className="text-white/30">·</span>
              <span>показів: {item.timesSeen}</span>
              <span className="text-white/30">·</span>
              <span>{formatDate(item.lastSeenAt)}</span>
            </div>
            <p className="mt-1.5 text-sm font-black">
              <QuestionText text={item.prompt} />
            </p>
            <p className="mt-1 font-mono text-[10px] text-white/35">
              {item.generatorId ?? 'банк'} · {item.itemId}
            </p>
          </div>
        ))}
      </div>
    </Panel>
  );
}
